import { Box, Typography, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";
import { useQueryClient } from "@tanstack/react-query";
import { Process } from "../../db_access/process";
import AddButton from "../button/AddButton";
import ColumnVisibilityButton from "../button/ColumnVisibilityButton";
import RemoveButton from "../button/RemoveButton";
import { TableHeaderContainer } from "../container/TableHeaderContainer";
import { TableHeaderToolsContainer } from "../container/TableHeaderToolsContainer";
import { TableTitleContainer } from "../container/TableTitleContainer";
import ColumnMenu from "../context-menu/ColumnMenu";
import { useContextProvider } from "../page-context/ContextProvider";
import AgGrid from "./AgGrid";
import { defaultColDef, floatFormater, formatTo2Decimals, leftAlignColDef, rightAlignColDef } from "./columnDefinitions/columnDefinitions";

function ProcessGrid({
  processes,
  setProcesses,
  selectedProcessIds,
  setSelectedProcessIds,
  isSupplier,
  editable,
  productID = null,
  projectID = null,
  buildingID = null,
  reusedProductID = null,
  buildingSection = null,
  isReuse = false,
  headerToolsLeft = null,
  maxHeight = undefined,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { isSaveClicked, setIsSaveVisible, setIsSaveClicked, setIsLoading, setRefresh, columnVisibilities } = useContextProvider();
  const queryClient = useQueryClient();
  const gridHeaderContainerRef = useRef(null);
  const gridRef = useRef(null);
  const colVisibility_key = "process_table_column_visibility";
  const [rowsToBeDeleted, setRowsToBeDeleted] = useState([]);
  const [columnMenuVisible, setColumnMenuVisible] = useState(false);

  const onCellValueChanged = (params) => {
    const { colDef, newValue, data } = params;
    setProcesses((prev) =>
      prev.map((p) => {
        if (p.id == data.id) {
          return { ...p, [colDef.field]: newValue, _isModified: true };
        } else {
          return p;
        }
      })
    );
    if (newValue !== undefined) {
      setIsSaveVisible(true);
    }
  };
  const onSelectionChanged = (event) => {
    const selected = event.api.getSelectedRows();
    setSelectedProcessIds(selected.map((s) => s.id));
  };
  
  // Programmatic selection visual sync
  useEffect(() => {
    if (gridRef.current && gridRef.current.api) {
      gridRef.current.api.forEachNode((node) => {
        const isSelected = selectedProcessIds.includes(node.data.id);
        if (node.isSelected() !== isSelected) {
          node.setSelected(isSelected);
        }
      });
    }
  }, [selectedProcessIds, processes]);

  const isOperationalCarbon = buildingSection?.subcategoryId === "operational_carbon";

  const lcaStageValues =
    isSupplier && !isReuse
      ? ["A1: Raw Material Acquisition", "A2: Transport to Factory", "A3: Manufacturing"]
      : buildingID
        ? isOperationalCarbon
          ? ["B6: Operational energy use", "B7: Operational water use"]
          : [
            "A1: Raw Material Acquisition",
            "A2: Transport to Factory",
            "A3: Manufacturing",
            "A4: Transport to Site",
            "A5: Construction-Installation",
            "B1: Use",
            "B2: Maintenance",
            "B3: Repair",
            "B4: Replacement",
            "B5: Refurbishment",
            "C1: Deconstuction / Demolition",
            "C2: Transport to Waste / Disposal",
            "C3: Waste Processing",
            "C4: Disposal of Waste",
            "D: Reuse-Recovery-Recycling Potential",
          ]
        : [
          "A1: Raw Material Acquisition",
          "A2: Transport to Factory",
          "A3: Manufacturing",
          "A4: Transport to Site",
          "A5: Construction-Installation",
          "B1: Use",
          "B2: Maintenance",
          "B3: Repair",
          "B4: Replacement",
          "B5: Refurbishment",
          "C1: Deconstuction / Demolition",
          "C2: Transport to Waste / Disposal",
          "C3: Waste Processing",
          "C4: Disposal of Waste",
          "D: Reuse-Recovery-Recycling Potential",
        ];

  const colDefs = useMemo(() => {
    const isHidden = (field) => !columnVisibilities[colVisibility_key]?.includes(field);
    return [
      {
        headerName: "LCA Stage",
        field: "lca_stage",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: lcaStageValues },
        sort: "asc",
        ...leftAlignColDef,
        ...defaultColDef,
        editable: (params) => editable && !params.data.readOnly,
        headerCheckboxSelection: true,
      },
      { headerName: "Name", field: "name", cellEditor: "agTextCellEditor", ...leftAlignColDef, ...defaultColDef, editable: (params) => editable && !params.data.readOnly },
      {
        headerName: "GWP Total (kg CO₂e)",
        field: "gwp",
        suppressMenu: true,
        ...rightAlignColDef,
        ...defaultColDef,
        ...floatFormater,
        editable: (params) => editable && !params.data.hasEnvFactor && !params.data.readOnly,
      },
      {
        headerName: "Biogenic GWP Total (kg CO₂e)",
        field: "biogenic_gwp",
        suppressMenu: true,
        ...floatFormater,
        ...rightAlignColDef,
        ...defaultColDef,
        editable: (params) => editable && !params.data.hasEnvFactor && !params.data.readOnly,
      },
    ].map((c) => ({ ...c, hide: isHidden(c.field) }));
  }, [columnVisibilities, editable, lcaStageValues]);
  const defaultCols = colDefs.map((col) => col.field);

  const totalGWP = useMemo(() => {
    const dataToSum = selectedProcessIds.length > 0 ? processes.filter((p) => selectedProcessIds.includes(p.id)) : processes;

    return formatTo2Decimals(
      dataToSum.reduce((total, row) => {
        const isStageD = row.lca_stage && row.lca_stage.trim().startsWith("D");
        if (isStageD) return total;
        const gwp = row.gwp && !isNaN(row.gwp) ? parseFloat(row.gwp) : 0;
        // Fossil GWP should only be positive GWP. Legacy/manual negative GWP entries are biogenic.
        return total + (gwp < 0 ? 0 : gwp);
      }, 0)
    );
  }, [selectedProcessIds, processes]);

  const biogenicTotalGWP = useMemo(() => {
    const dataToSum = selectedProcessIds.length > 0 ? processes.filter((p) => selectedProcessIds.includes(p.id)) : processes;

    return formatTo2Decimals(
      dataToSum.reduce((total, row) => {
        const isStageD = row.lca_stage && row.lca_stage.trim().startsWith("D");
        if (isStageD) return total;
        const gwp = row.gwp && !isNaN(row.gwp) ? parseFloat(row.gwp) : 0;
        const biogenicGWP = row.biogenic_gwp && !isNaN(row.biogenic_gwp) ? parseFloat(row.biogenic_gwp) : 0;
        // Sum biogenic_gwp. If biogenic_gwp is 0 but gwp is negative (legacy entry), treat that negative gwp as biogenic.
        const rowBiogenic = biogenicGWP !== 0 ? biogenicGWP : (gwp < 0 ? gwp : 0);
        return total + rowBiogenic;
      }, 0)
    );
  }, [selectedProcessIds, processes]);

  const addProcess = () => {
    let defaultProcess = {
      biogenic_gwp: 0.0,
      design_id: null,
      product_id: null,
      building_id: null,
      building_section_category: null,
      building_section_subcategory: null,
      gwp: 0,
      lca_stage: isOperationalCarbon ? "B6: Operational energy use" : "A1: Raw Material Acquisition",
      name: "New Process",
      reused_product_id: null,
      _isModified: true,
      id: uuidv4(),
    };

    if (isSupplier) {
      if (isReuse) {
        defaultProcess.reused_product_id = reusedProductID;
      } else {
        defaultProcess.product_id = productID;
      }
    } else if (buildingID) {
      defaultProcess.building_id = buildingID;
      if (buildingSection) {
        defaultProcess.building_section_category = buildingSection.categoryId;
        defaultProcess.building_section_subcategory = buildingSection.subcategoryId;
      }
    } else if (projectID) {
      defaultProcess.design_id = projectID;
    }

    setProcesses((prev) => [...prev, defaultProcess]);
    setIsSaveVisible(true);
  };
  useEffect(() => {
    if (isSaveClicked) {
      const promises = [];
      const requestData = [];
      processes.forEach((p) => {
        if (p._isModified) {
          const { _isModified, ...process } = p;
          requestData.push(process);
        }
      });
      if (requestData.length > 0) {
        const savePromise = Process.save(requestData).then((res) => {
          if (!res.ok) throw new Error("Save failed");
          return res.json();
        });
        promises.push(savePromise);
      }
      if (rowsToBeDeleted.length > 0) {
        const deletePromise = Process.delete(rowsToBeDeleted.map((i) => i.id)).then((res) => {
          if (!res.ok) throw new Error("Delete failed");
          return res.json();
        });
        promises.push(deletePromise);
      }
      Promise.all(promises)
        .then(([saveResult, deleteResult]) => { })
        .catch((err) => {
          console.error("Error during save/delete:", err);
        })
        .finally(() => {
          setIsSaveClicked(false);
          setIsLoading(false);
          setIsSaveVisible(false);
          setRowsToBeDeleted([]);
          setRefresh((r) => !r);

          // Invalidate cached process data and results for the current entity to refresh Result tab
          if (productID) {
            queryClient.invalidateQueries({ queryKey: ["product", productID, "process"] });
          } else if (projectID) {
            queryClient.invalidateQueries({ queryKey: ["project", projectID, "process"] });
          } else if (buildingID) {
            queryClient.invalidateQueries({ queryKey: ["building", buildingID, "process"] });
            queryClient.invalidateQueries({ queryKey: ["building", buildingID, "results"] });
          }
        });
    }
  }, [isSaveClicked, productID, projectID, buildingID, queryClient]);

  const footerRight = (
    <Box sx={{ display: "flex", gap: 3, alignItems: "center", pr: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? "#a1a1aa" : "#64748b" }}>
        Total Biogenic: <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: isDark ? "#ffffff" : "#0f172a" }}>{biogenicTotalGWP}</Typography>
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 500, color: isDark ? "#a1a1aa" : "#64748b" }}>
        Total Fossil: <Typography component="span" variant="body2" sx={{ fontWeight: 700, color: isDark ? "#ffffff" : "#0f172a" }}>{totalGWP}</Typography>
      </Typography>
    </Box>
  );

  return (
    <Box>
      <TableHeaderContainer ref={gridHeaderContainerRef}>
        <TableTitleContainer>
          <Typography variant="Table Title">{t("Process")}</Typography>
        </TableTitleContainer>
        <TableHeaderToolsContainer>
          {headerToolsLeft}
          <Box sx={{ display: "flex", gap: 1.5, alignItems: "center" }}>
            <AddButton
              onClick={() => {
                addProcess();
              }}
              disabled={isSupplier ? (isReuse ? !reusedProductID : !productID) : !projectID && !buildingID}
            />

            <RemoveButton
              onClick={() => {
                const processesToDelete = processes.filter((p) => selectedProcessIds.includes(p.id));
                setProcesses((prev) => prev.filter((a) => !selectedProcessIds.includes(a.id)));
                setRowsToBeDeleted((prev) => [...prev, ...processesToDelete]);
                setIsSaveVisible(true);
              }}
              disabled={selectedProcessIds.length < 1}
            />
            <ColumnVisibilityButton onClick={() => setColumnMenuVisible((p) => !p)} />
          </Box>
        </TableHeaderToolsContainer>
      </TableHeaderContainer>
      <AgGrid
        ref={gridRef}
        rowData={processes}
        columnDefs={colDefs}
        onSelectionChanged={onSelectionChanged}
        onCellValueChanged={onCellValueChanged}
        headerCheckbox={false}
        colVisibility_key={colVisibility_key}
        footerRightContent={footerRight}
        maxHeight={maxHeight}
        pagination={true}
        hidePaginationControls={true}
        domLayout="autoHeight"
      />
      <ColumnMenu
        anchorRef={gridHeaderContainerRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={colDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
        gridRef={gridRef}
      />
    </Box>
  );
}

export default ProcessGrid;
